import { useState, useEffect, useCallback } from "react"

import gsap from "gsap"
import styled from "styled-components"

import colors from "styles/colors"
import media from "styles/media"
import text from "styles/text"

interface Props {
  bannerText: React.ReactNode | string
  position?: string
  overrideMargin?: string
  specialOpen?: boolean
  setSpecialOpen?: React.Dispatch<React.SetStateAction<number>>
  small?: boolean
  index?: number
  autoHeight?: boolean
}

export default function ToolTip({
  bannerText,
  position = "left",
  overrideMargin = "",
  specialOpen = false,
  setSpecialOpen,
  small = false,
  index = 0,
  autoHeight = false,
}: Props) {
  const [bannerPosition, setBannerPosition] = useState(``)
  const [tagPosition, setTagPosition] = useState("")
  const [bannerEl, setBannerEl] = useState<HTMLDivElement | null>(null)
  const [bannerOpen, setBannerOpen] = useState<boolean>(false)

  useEffect(() => {
    switch (position) {
      case "center":
        setBannerPosition(`
          right: 50%;
          transform: translateX(50%);
        `)
        setTagPosition(`
          right: 50%;
          transform: translateX(50%);
        `)
        break

      case "right":
        break

      case "left":
      default:
        setBannerPosition(`
          ${media.fullWidth} {
            right: -15px;
          }

          ${media.desktop} {
            right: -0.9vw;
          }

          ${media.tablet} {
            right: 0vw;
          }

          ${media.mobile} {
            right: -3.73vw;
          }
        `)
        setTagPosition(`
          ${media.fullWidth} {
            right: 15px;
          }
          ${media.desktop} {
            right: 0.9vw;
          }

          ${media.tablet} {
            right: 1.4vw;
          }

          ${media.mobile} {
            right: 3.5vw;
          }
        `)
        break
    }
  }, [position, setBannerPosition, setTagPosition])

  const open = useCallback(() => {
    if (bannerEl) {
      gsap.set(bannerEl, {
        display: "flex",
      })

      gsap.to(bannerEl, {
        opacity: 1,
      })
    }
  }, [bannerEl])

  const close = useCallback(() => {
    if (bannerEl) {
      gsap.to(bannerEl, {
        opacity: 0,
        onComplete: () => {
          gsap.set(bannerEl, {
            display: "none",
          })
        },
      })
    }
  }, [bannerEl])

  useEffect(() => {
    if (bannerOpen) {
      if (setSpecialOpen && index) {
        setSpecialOpen(index)
      }
      open()
    } else {
      close()
    }
  }, [bannerOpen, open, close, setSpecialOpen, index])

  useEffect(() => {
    if (specialOpen) {
      setBannerOpen(true)
    } else {
      setBannerOpen(false)
    }
  }, [specialOpen, setBannerOpen])

  const handleClick = () => {
    if (!specialOpen) setBannerOpen(value => !value)
  }

  return (
    <Wrapper>
      <BannerWrapper ref={ref => setBannerEl(ref)} position={bannerPosition}>
        <Banner>
          <BannerText>{bannerText}</BannerText>
        </Banner>
        <Tag width="20" height="10" viewBox="0 0 20 10" position={tagPosition}>
          <path
            d="M 0 0 L 8 9 C 10 11 10 11 12 9 L 20 0 Z"
            fill={colors.blue400}
          />
        </Tag>
      </BannerWrapper>
      <IconWrapper
        small={small}
        overrideMargin={overrideMargin}
        onClick={handleClick}
        autoHeight={autoHeight}
      >
        <SVG viewBox="0 0 20 20" fill="none">
          <rect width="20" height="20" rx="10" fill={colors.blue400} />
          <path
            d="M10 8.5L10 14.5M10 6L10 5.5"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </SVG>
      </IconWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: relative;
  a {
    color: ${colors.white};
  }
`

const BannerWrapper = styled.div<{ position: string }>`
  position: absolute;
  display: none;
  z-index: 2;
  opacity: 0;
  ${props => props.position}

  ${media.fullWidth} {
    box-shadow: 0px 15px 30px rgba(22, 50, 145, 0.12);
    bottom: 50px;
    width: 265px;
  }

  ${media.desktop} {
    box-shadow: 0vw 0.9vw 1.81vw rgba(22, 50, 145, 0.12);
    bottom: 3.01vw;
    width: 15.96vw;
  }

  ${media.tablet} {
    box-shadow: 0vw 1.46vw 2.93vw rgba(22, 50, 145, 0.12);
    width: 25.88vw;
    bottom: 5vw;
  }

  ${media.mobile} {
    box-shadow: 0vw 4vw 8vw rgba(22, 50, 145, 0.12);
    width: 70.67vw;
    bottom: 15.73vw;
  }
`

const Banner = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${colors.blue400};

  ${media.fullWidth} {
    border-radius: 4px;
  }

  ${media.desktop} {
    border-radius: 0.24vw;
  }

  ${media.tablet} {
    border-radius: 0.39vw;
  }

  ${media.mobile} {
    border-radius: 1.07vw;
  }
`

const BannerText = styled.div`
  width: 100%;
  height: 100%;
  text-align: center;
  ${text.bodyXS};
  color: ${colors.white};
  letter-spacing: 0.03vw;
  a {
    text-decoration: underline;
  }
  ${media.fullWidth} {
    padding: 6px 10px;
    letter-spacing: 0.5px;
  }

  ${media.desktop} {
    padding: 0.36vw 0.6vw;
  }

  ${media.tablet} {
    padding: 0.59vw 0.98vw;
  }

  ${media.mobile} {
    padding: 1.6vw 2.67vw;
  }
`

const Tag = styled.svg<{ position: string }>`
  position: absolute;
  ${props => props.position}

  ${media.fullWidth} {
    bottom: -9px;
  }

  ${media.desktop} {
    bottom: -0.5vw;
  }

  ${media.tablet} {
    bottom: -0.9vw;
  }

  ${media.mobile} {
    bottom: -2.5vw;
  }
`

const IconWrapper = styled.div<{
  small?: boolean
  overrideMargin?: string
  autoHeight?: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;

  ${media.tablet} {
    width: ${({ overrideMargin, autoHeight }) =>
      autoHeight ? "initial" : overrideMargin ? "fit-content" : "4.88vw"};
    height: ${props => (props.autoHeight ? "initial" : "4.88vw")};
  }

  ${media.mobile} {
    width: ${({ small, autoHeight }) =>
      autoHeight ? "initial" : small ? "unset" : "13.33vw"};
    height: ${({ small, autoHeight }) =>
      autoHeight ? "initial" : small ? "unset" : "13.33vw"};
    svg {
      width: ${({ small }) => (small ? "3vw" : "5.33vw")};
      height: ${({ small }) => (small ? "3vw" : "5.33vw")};
    }
  }
`

const SVG = styled.svg`
  cursor: pointer;

  ${media.fullWidth} {
    width: 20px;
    height: 20px;
  }

  ${media.desktop} {
    width: 1.2vw;
    height: 1.2vw;
  }

  ${media.tablet} {
    width: 1.95vw;
    height: 1.95vw;
  }

  ${media.mobile} {
    width: 5.33vw;
    height: 5.33vw;
  }
`
